import { Component, OnInit, inject } from "@angular/core";
import { TabModel } from "@models/generic/TabModels/TabModel";
import { ThirdPartyDataService } from "../services/ThirdPartyDataService";
import { FilterConfig } from "@models/workspace/FilterConfig";
import { FilterType } from "@enums/FilterType";
import { RendererConfig } from "@models/workspace/RendererConfig";
import { RendererType } from "@enums/RendererType";
import { TabService } from "@services/tabs/TabService";
import { Pages } from "@constants/pages/Pages";
import { ModuleType } from "@enums/ModuleType";
import { FormatType } from "@enums/FormatType";
import { PermissionsService } from "@services/permissions/PermissionsService";
import { StandingdataDataService } from "@app/standingdata/shared/standingdata.service";
import { StandingDataType } from "@enums/StandingDataType";
import { CustomFieldHelper } from "@app/shared/helpers/customField-helper";
import { WorkspaceModulePermissionModel } from "@models/permissions/WorkspaceModulePermissions";
import { firstValueFrom } from "rxjs";
import { WorkspaceComponent } from "@app/shared/workspace/workspace.component";
import { TranslatePipe, injectTranslation, provideTranslationScope } from "@app/shared/i18n/translation-scope";
import { scope } from "@app/thirdparty/i18n/scope";

@Component({
    selector: "thirdparty-overview",
    templateUrl: "./thirdparty-overview.component.html",
    styleUrls: ["./thirdparty-overview.component.scss"],
    imports: [WorkspaceComponent, TranslatePipe],
    viewProviders: [provideTranslationScope(scope)],
})
export class ThirdPartyOverviewComponent implements OnInit {
    private tabService = inject(TabService);
    private thirdpartyDataService = inject(ThirdPartyDataService);
    private _permissions = inject(PermissionsService);
    private pages = inject(Pages);
    private standingDataService = inject(StandingdataDataService);
    private _translation = injectTranslation(scope);

    moduleType = ModuleType.TP;
    tabID: string;
    tab: TabModel;
    data: Promise<any[]>;
    headerLookup: any = {};

    filterConfigs: FilterConfig[] = [
        {
            label: "Organizations Responsible",
            column: "OrganizationResponsibleID",
            type: FilterType.Organization,
            hideActionColumn: true,
            param: "",
        },
        {
            label: "Business dimensions",
            column: "BusinessDimensionIds",
            type: FilterType.BusinessDimensions,
            hideActionColumn: true,
            param: "",
        },
        {
            label: "Framework dimensions",
            column: "FrameworkDimensionIds",
            type: FilterType.FrameworkDimensions,
            hideActionColumn: true,
            param: "",
        },
    ];
    rendererConfig: RendererConfig[] = [
        {
            textColumn: "Review score",
            actionColumn: "Latest_Score_Color",
            hideActionColumn: true,
            type: RendererType.Score,
        },
        {
            textColumn: "Criticality",
            actionColumn: "CriticalityColor",
            hideActionColumn: true,
            type: RendererType.Score,
        },
        {
            textColumn: "Next review date",
            type: RendererType.Default,
            formatType: FormatType.DateFormat,
        },
        {
            textColumn: "Date reviewed",
            type: RendererType.Default,
            formatType: FormatType.DateFormat,
        },
        {
            textColumn: "Start date servicing",
            type: RendererType.Default,
            formatType: FormatType.DateFormat,
        },
        {
            textColumn: "End date servicing",
            type: RendererType.Default,
            formatType: FormatType.DateFormat,
        },
    ];

    numberProps: string[] = [];
    dateProps: string[] = [
        "Next review date",
        "Date reviewed",
        "Start date servicing",
        "End date servicing",
    ];

    permissions: WorkspaceModulePermissionModel;

    async ngOnInit() {
        this.loadPermissions();

        // The template creates cerrix-workspace only once `data` is set, so the labels below
        // are translated before the workspace reads them.
        await this._translation.ready();
        const t = (key: string) => this._translation.t(key);
        this.filterConfigs[0].label = t("overview.filterOrganizationsResponsible");
        this.filterConfigs[1].label = t("overview.filterBusinessDimensions");
        this.filterConfigs[2].label = t("overview.filterFrameworkDimensions");
        // Keyed by the column's property name in the data; the value is the visible header.
        this.headerLookup = {
            Identifier: t("overview.identifierHeader"),
            Name: t("overview.nameHeader"),
            "Review score": t("overview.reviewScoreHeader"),
            "Date reviewed": t("overview.dateReviewedHeader"),
            "Next review date": t("overview.nextReviewDateHeader"),
            Reviewer: t("overview.reviewerHeader"),
            "Reviewer comment": t("overview.reviewerCommentHeader"),
            "Organization responsible": t("overview.organizationResponsibleHeader"),
            Responsible: t("overview.responsibleHeader"),
            Address: t("overview.addressHeader"),
            City: t("overview.cityHeader"),
            Country: t("overview.countryHeader"),
            Email: t("overview.emailHeader"),
            Phone: t("overview.phoneHeader"),
            Website: t("overview.websiteHeader"),
            Description: t("overview.descriptionHeader"),
            "EU-member": t("overview.euMemberHeader"),
            "D-U-N-S number": t("overview.dunsNumberHeader"),
            "Authorized signatory": t("overview.authorizedSignatoryHeader"),
            "Chamber of commerce": t("overview.chamberOfCommerceHeader"),
            "Value added tax number (VAT)": t("overview.vatNumberHeader"),
            "Third party responsible": t("overview.thirdPartyResponsibleHeader"),
            "Status of involvement": t("overview.statusOfInvolvementHeader"),
            Services: t("overview.servicesHeader"),
            Criticality: t("overview.criticalityHeader"),
            "Start date servicing": t("overview.startDateServicingHeader"),
            "End date servicing": t("overview.endDateServicingHeader"),
            Relationship: t("overview.relationshipHeader"),
            Comment: t("overview.commentHeader"),
            "Organization beneficial owner": t("overview.organizationBeneficialOwnerHeader"),
            "Beneficial owner": t("overview.beneficialOwnerHeader"),
            "Organizations in use": t("overview.organizationsInUseHeader"),
            "Business dimensions": t("overview.businessDimensionsHeader"),
            "Framework dimensions": t("overview.frameworkDimensionsHeader"),
            Contacts: t("overview.contactsHeader"),
            "# of contracts": t("overview.numberOfContractsHeader"),
            "LEI code": t("overview.leiCodeHeader"),
        };

        this.data = this.loadData();
    }

    loadPermissions() {
        this.permissions = this._permissions.permissions.Workspace.thirdParty;
    }

    public openThirdParty(thirdparty: any) {
        this.tab.showLoader = true;
        this.tabService.generateTab(
            this.pages.ThirdpartyDetail,
            thirdparty.Guid,
            `(TP) ${thirdparty.Identifier} - ${thirdparty.Name}`,
            null
        );
        this.tab.showLoader = false;
    }

    public async addThirdParty() {
        await this._translation.ready();
        this.tabService.generateTab(
            this.pages.ThirdpartyDetail,
            null,
            this._translation.t("detail.addNewThirdParty"),
        );
    }

    private async loadData() {
        const data$ = firstValueFrom(this.thirdpartyDataService.getThirdParties());
        const data = await data$;
        await this.expandStandingDataCustomFields(data);
        return this.mapHeaders(data);
    }

    private mapHeaders = (rows) => {
        if (rows && rows.length) {
            Object.getOwnPropertyNames(rows[0]).forEach((propName) => {
                if (!this.headerLookup[propName]) {
                    this.headerLookup[propName] = propName;
                }
            });
        }
        return rows;
    };

    private async expandStandingDataCustomFields(data: Object[]) {
        const expandableFields = (
            await firstValueFrom(this.standingDataService
                .getAllByType(StandingDataType.ThirdPartyCustomFields))
        ).filter((x) => x.visible);

        CustomFieldHelper.expandCustomFields(
            data,
            "customFields",
            expandableFields,
            this.headerLookup,
            this.rendererConfig
        );
    }
}
